import { usePathname, useSearchParams, useRouter } from "next/navigation";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  Suspense,
} from "react";

import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { isServer } from "@/src/lib/runtime";
import { getSessionItem, setSessionItem } from "@/src/lib/session-storage";

import type { ParserMap, inferParserType } from "nuqs";

const TABLE_FILTERS_PREFIX = "table-filters:";

function getTableFiltersKey(path: string): string {
  return `${TABLE_FILTERS_PREFIX}${path}`;
}

const DASHBOARD_BASE_PATHS = [
  "/appointments",
  "/authorizations",
  "/files",
  "/inbox",
  "/leads",
  "/locations",
  "/orders",
  "/patients",
  "/preferred-providers",
  "/providers",
  "/referrals",
  "/tasks",
  "/voice",
] as const;

export function getBasePath(pathname: string): string {
  for (const base of DASHBOARD_BASE_PATHS) {
    if (pathname === base || pathname.startsWith(`${base}/`)) {
      return base;
    }
  }

  return pathname;
}

function isDashboardPath(pathname: string): boolean {
  return DASHBOARD_BASE_PATHS.some((base) => pathname === base || pathname.startsWith(`${base}/`));
}

interface DataTableSearchContext {
  storedParamsByPath: Record<string, URLSearchParams>;
  storeSearchContext: () => void;
  setStoredParams: (path: string, params: URLSearchParams) => void;
}

const DataTableSearchContext = createContext<DataTableSearchContext>({
  storedParamsByPath: {},
  storeSearchContext: () => {},
  setStoredParams: () => {},
});

export function useDataTableSearchContext() {
  return useContext(DataTableSearchContext);
}

function QueryStringSessionSyncGate({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const hasSeenNonEmptyByPathRef = useRef<Record<string, boolean>>({});

  const basePath = getBasePath(pathname);
  const hasParams = searchParams.toString().length > 0;
  const isListPage = isDashboardPath(pathname) && pathname === basePath;

  const storedQuery = isListPage && !isServer ? getSessionItem(getTableFiltersKey(basePath))?.trim() || null : null;

  const needsRestore = isListPage && !hasParams && !!storedQuery && !hasSeenNonEmptyByPathRef.current[basePath];

  useLayoutEffect(() => {
    if (isServer || !pathname || !isDashboardPath(pathname)) return;

    const basePath = getBasePath(pathname);
    const qs = searchParams.toString();
    const key = getTableFiltersKey(basePath);

    if (pathname !== basePath) {
      delete hasSeenNonEmptyByPathRef.current[basePath];

      return;
    }

    if (qs.length > 0) {
      hasSeenNonEmptyByPathRef.current[basePath] = true;
      setSessionItem(key, qs);

      return;
    }

    if (hasSeenNonEmptyByPathRef.current[basePath]) {
      setSessionItem(key, "");

      return;
    }

    const s = getSessionItem(key);

    if (!s?.trim()) return;

    router.replace(`${basePath}?${s.trim()}`);
  }, [pathname, searchParams, router]);

  if (needsRestore) {
    return <Skeleton className="h-[50vh] w-full" />;
  }

  return <>{children}</>;
}

function DataTableSearchParamsSync({
  setStoredParamsByPath,
}: {
  setStoredParamsByPath: React.Dispatch<React.SetStateAction<Record<string, URLSearchParams>>>;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isServer || !pathname) return;

    const basePath = getBasePath(pathname);

    if (pathname !== basePath) return;

    const params = new URLSearchParams(searchParams.toString());

    setStoredParamsByPath((prev) => {
      const currentParamsString = params.toString();
      const prevParamsString = prev[basePath]?.toString();

      if (currentParamsString === prevParamsString) return prev;

      return { ...prev, [basePath]: params };
    });
  }, [pathname, searchParams, setStoredParamsByPath]);

  return null;
}

export function DataTableSearchProvider({ children }: PropsWithChildren) {
  const [storedParamsByPath, setStoredParamsByPath] = useState<Record<string, URLSearchParams>>({});

  const storeSearchContext = useCallback(() => {
    if (isServer) return;
    const { pathname } = window.location;
    const basePath = getBasePath(pathname);

    if (isDashboardPath(pathname) && pathname !== basePath) return;

    if (window.location.pathname !== basePath) return;

    setStoredParamsByPath((prev) => ({
      ...prev,
      [basePath]: new URLSearchParams(window.location.search),
    }));
  }, []);

  const setStoredParams = (path: string, params: URLSearchParams) => {
    setSessionItem(getTableFiltersKey(path), params.toString());
    setStoredParamsByPath((prev) => ({ ...prev, [path]: params }));
  };

  const ctx = {
    storedParamsByPath,
    storeSearchContext,
    setStoredParams,
  };

  return (
    <DataTableSearchContext.Provider value={ctx}>
      <Suspense fallback={null}>
        <QueryStringSessionSyncGate>
          <DataTableSearchParamsSync setStoredParamsByPath={setStoredParamsByPath} />
          {children}
        </QueryStringSessionSyncGate>
      </Suspense>
    </DataTableSearchContext.Provider>
  );
}

function subscribeToStorage(listener: () => void) {
  window.addEventListener("storage", listener);

  return () => window.removeEventListener("storage", listener);
}

export function useStoredParams(path: string): URLSearchParams | undefined {
  const { storedParamsByPath } = useDataTableSearchContext();
  const persistedQuery = useSyncExternalStore(
    subscribeToStorage,
    () => getSessionItem(getTableFiltersKey(path))?.trim() || null,
    () => null
  );

  return storedParamsByPath[path] ?? (persistedQuery ? new URLSearchParams(persistedQuery) : undefined);
}

export function useDataTableUrl(path: string) {
  const searchParams = useStoredParams(path);

  if (!searchParams || searchParams.size === 0) {
    return path;
  }

  return `${path}?${searchParams.toString()}`;
}

export function useDataTableFilters<Parsers extends ParserMap>(
  path: string,
  parsers: Parsers
): inferParserType<Parsers> {
  const searchParams = useStoredParams(path);
  const pathname = usePathname();
  const isMatchingPath = getBasePath(pathname) === path;

  return (() => {
    const result: Record<string, unknown> = {};

    for (const [key, parser] of Object.entries(parsers)) {
      const value = searchParams?.get(key);

      result[key] =
        isMatchingPath && value != null && typeof value === "string"
          ? (parser as { parse(queryValue: string): unknown }).parse(value)
          : null;
    }

    return result as inferParserType<Parsers>;
  })();
}
